import type { LoaderFunction, MetaFunction } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import { useLoaderData, useRouteLoaderData } from '@remix-run/react';

import GatedPage from '~/pages/pages/GatedPage';
import PageHtml from '~/pages/pages/PageHTML';
import PageWrapper from '~/pages/pages/PageWrapper';

import Breadcrumbs from '~/components/Breadcrumbs';
import BlueskyEmbed from '~/components/Scripts/BlueskyEmbed';
import StocktwitsEmbed from '~/components/Scripts/StocktwitsEmbed';
import TwitterEmbed from '~/components/Scripts/TwitterEmbed';

import getCustomPage from '~/services/api/custom_pages/getCustomPage.server';
import getPaywall from '~/services/api/paywalls/getPaywall.server';
import getPublication from '~/services/api/publication/getPublication.server';

import getCanonicalUrl from '~/utils/getCanonicalUrl';
import getMergedMetaData from '~/utils/getMergedMetaData';
import normalizeSlug from '~/utils/normalizeSlug';

import { loaderSession } from '~/session';

export const meta: MetaFunction<typeof loader> = ({ matches, data }) => {
  const publication = data?.publication;
  const page = data?.page;
  const title = page?.meta_title || page?.title;
  const description = page?.meta_description || publication.description;
  const thumbnail = page?.thumbnail || publication?.thumbnail?.url || publication?.logo?.url;

  return getMergedMetaData({
    canonicalUrl: getCanonicalUrl(data?.requestUrl),
    matches,
    returnEarlyCheck: !page,
    overrides: {
      title: `${title} | ${publication.name}`,
      description,
      'og:url': `${publication.url}c/${page?.slug}`,
      'og:description': description,
      'og:site_name': `${publication?.name}`,
      'og:image': `${thumbnail}`,
      'og:image:alt': `${title || publication?.description}`,
      'twitter:url': `${publication.url}p/${page?.slug}`,
      'twitter:title': `${title} | ${publication.name}`,
      'twitter:description': description,
      'twitter:image': `${thumbnail}`,
      robots: page.noindexing_enabled ? 'noindex' : undefined,
    },
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@id': publication?.url,
              name: publication?.name,
            },
          },
        ],
      },
    },
  });
};

export const loader: LoaderFunction = async ({ request, params }: any) => {
  const { session } = await loaderSession(request);
  const pageSlugKey = params.page;
  const normalizedSlugKey = normalizeSlug(pageSlugKey);
  const url = new URL(request.url);

  const [publication, page] = await Promise.all([
    getPublication(request, session),
    getCustomPage(request, session, normalizedSlugKey),
  ]);

  const isPremiumPage = page.premium;
  let paywall = null;
  if (isPremiumPage) {
    paywall = await getPaywall(request, session);
  } // Add this closing brace

  if (!publication.has_pages || page.redirect) {
    return redirect('/');
  }

  return json({
    requestUrl: url.href,
    publication,
    page,
    paywall,
  });
};

export default function Page() {
  const { publication, page, paywall } = useLoaderData<typeof loader>();
  const { subscription } = useRouteLoaderData<typeof loader>('root');
  const showPaywall = page?.premium && subscription?.tier !== 'premium';

  const breadCrumbs = [
    {
      text: publication?.name,
      href: '../',
    },
    {
      text: 'Pages',
      href: undefined,
    },
    {
      text: page.title,
      href: undefined,
    },
  ];

  return (
    <div className="mx-auto min-h-screen w-full max-w-6xl px-4">
      {showPaywall ? (
        <GatedPage publication={publication} subscription={subscription} paywall={paywall} />
      ) : (
        <>
          <PageWrapper>
            {publication.render_breadcrumbs && (
              <div className="mx-auto mt-8 w-full max-w-2xl">
                <Breadcrumbs breadcrumbs={breadCrumbs} />
              </div>
            )}
            <PageHtml html={page.html} slug={page.slug} />
          </PageWrapper>

          <TwitterEmbed updateOn={true} />
          <BlueskyEmbed updateOn={true} />
          <StocktwitsEmbed />
        </>
      )}
    </div>
  );
}
